/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * A grouped report pivoted into a cross-tab.
 *
 * `matrix` was in the migration's CHECK constraint and in the schemas from the
 * start but nothing produced one, so it was a promise the UI could not keep.
 * This makes it real without touching the engine: a report grouped by two
 * fields already returns every (row, column, measure) combination, so the
 * pivot is presentation. The engine stays one code path.
 *
 * Deliberately client-side. Pivoting in SQL means either a crosstab extension
 * or dynamic column generation from the data, and both make the column set
 * depend on the result — which the self-describing response contract does not
 * allow for.
 */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography,
} from '@mui/material';

import colors from 'styles/tokens/colors';
import { displayValue, formatValue } from './chartPalette';

export default function ReportMatrix({ result, currency, maxHeight }) {
  const model = useMemo(() => pivot(result, result?.labels), [result]);

  if (!model) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        A matrix needs a report grouped by two fields with at least one
        summary — one grouping becomes the rows, the other the columns.
        Open Edit and add a second grouping.
      </Alert>
    );
  }

  const { rowLabels, columnLabels, cells, rowTotals, columnTotals, grand,
          measure, rowColumn, columnColumn } = model;

  const cell = (value) => formatValue(value, measure, currency);

  return (
    <Box>
      <TableContainer
        sx={{
          maxHeight: maxHeight || 'none',
          borderRadius: '12px',
          // Same overflow:hidden theme clip as ReportTable — a capped matrix
          // must scroll instead of hiding rows.
          ...(maxHeight ? { '&&': { overflowX: 'auto', overflowY: 'auto' } } : {}),
        }}
      >
        <Table stickyHeader size="small">
          {/* The app's vendor theme (assets/theme/components/table/tableHead.js)
            globally sets MuiTableHead to display:block with padding and a
            border radius, for the card-style tables Material Dashboard ships.
            That takes the header row out of the table's column-width
            calculation, so every th sizes to its own content while the body
            cells size to the table — the header ends up a narrow pill above
            full-width rows.

            Restored locally rather than by changing the global, which ~160
            existing pages render against. */}
        <TableHead sx={{ display: 'table-header-group', p: 0, borderRadius: 0 }}>
            <TableRow>
              <TableCell sx={{ ...headSx, position: 'sticky', left: 0, zIndex: 3 }}>
                {rowColumn.label} ↓ / {columnColumn.label} →
              </TableCell>
              {columnLabels.map((label) => (
                <TableCell key={label} align="right" sx={headSx}>{label}</TableCell>
              ))}
              <TableCell align="right" sx={{ ...headSx, fontWeight: 800 }}>Total</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rowLabels.map((rowLabel) => (
              <TableRow key={rowLabel} hover>
                <TableCell
                  sx={{
                    fontWeight: 600, whiteSpace: 'nowrap',
                    position: 'sticky', left: 0, zIndex: 1,
                    backgroundColor: colors.background.white,
                  }}
                >
                  {rowLabel}
                </TableCell>
                {columnLabels.map((columnLabel) => {
                  const value = cells[rowLabel]?.[columnLabel];
                  return (
                    <TableCell
                      key={columnLabel} align="right"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        // An absent combination is not zero — nothing was
                        // grouped there. Rendering 0 would invite a reader to
                        // treat it as a measured value.
                        color: value === undefined ? colors.text.light : 'inherit',
                      }}
                    >
                      {value === undefined ? '—' : cell(value)}
                    </TableCell>
                  );
                })}
                <TableCell align="right"
                           sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {cell(rowTotals[rowLabel])}
                </TableCell>
              </TableRow>
            ))}

            <TableRow>
              <TableCell sx={{ ...totalSx, position: 'sticky', left: 0, zIndex: 1 }}>
                Total
              </TableCell>
              {columnLabels.map((label) => (
                <TableCell key={label} align="right" sx={totalSx}>
                  {cell(columnTotals[label])}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...totalSx, fontWeight: 800 }}>
                {cell(grand)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap"
           sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>{measure.label}</strong>, {rowLabels.length} row(s) &times;{' '}
          {columnLabels.length} column(s)
        </Typography>
        {result?.truncated && (
          <Chip
            size="small"
            label={`Built from the first ${result.limit.toLocaleString()} groups — totals are partial`}
            sx={{ backgroundColor: colors.status.warning.background,
                  color: colors.status.warning.text, fontWeight: 600 }}
          />
        )}
      </Box>
    </Box>
  );
}

ReportMatrix.propTypes = {
  result: PropTypes.object,
  currency: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const headSx = {
  fontWeight: 700, whiteSpace: 'nowrap',
  backgroundColor: colors.background.offWhite, color: colors.text.dark,
};

const totalSx = {
  fontWeight: 700, whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  backgroundColor: colors.background.offWhite, color: colors.text.dark,
};

/**
 * Turn a two-grouping result into rows, columns and cells, or null.
 *
 * Returns null rather than throwing when the shape does not fit, so the caller
 * can explain what is missing instead of losing the page to an error boundary.
 */
function pivot(result, labels) {
  const columns = result?.columns ?? [];
  const rows = result?.rows ?? [];
  if (!rows.length) return null;

  const dimensions = columns.filter((c) => !c.is_aggregate);
  const measures = columns.filter((c) => c.is_aggregate);
  if (dimensions.length < 2 || !measures.length) return null;

  const [rowColumn, columnColumn] = dimensions;
  const measure = measures[0];

  const cells = {};
  const rowTotals = {};
  const columnTotals = {};
  const rowOrder = [];
  const columnOrder = [];
  let grand = 0;

  rows.forEach((row) => {
    const rowLabel = displayValue(row[rowColumn.key], rowColumn, undefined, labels) || 'Blank';
    const columnLabel = displayValue(row[columnColumn.key], columnColumn, undefined, labels) || 'Blank';
    const raw = Number(row[measure.key] ?? 0);
    const value = Number.isFinite(raw) ? raw : 0;

    if (!(rowLabel in cells)) { cells[rowLabel] = {}; rowOrder.push(rowLabel); }
    if (!columnOrder.includes(columnLabel)) columnOrder.push(columnLabel);

    // Sum rather than assign: a definition with a third grouping produces
    // several rows per cell, and overwriting would show one of them as if it
    // were the total.
    cells[rowLabel][columnLabel] = (cells[rowLabel][columnLabel] ?? 0) + value;
    rowTotals[rowLabel] = (rowTotals[rowLabel] ?? 0) + value;
    columnTotals[columnLabel] = (columnTotals[columnLabel] ?? 0) + value;
    grand += value;
  });

  return {
    rowLabels: rowOrder, columnLabels: columnOrder, cells,
    rowTotals, columnTotals, grand, measure, rowColumn, columnColumn,
  };
}
